import api from "../proxy/config";
import axios from "./axiosConfig";
import {
  articleSavePayloadSchema,
  bannerUpdatePayloadSchema,
  imageScopeSchema,
  parseArticle,
  parseArticleCategories,
  parseArticleSummaries,
  parseBanner,
  parseBanners,
  parseStorageObjectSummaries,
  parseStorageUploadResult,
  formatSchemaParseError,
} from "./schemas/contentSchemas";
import { sanitizeParagraphRunsForSave } from "../utils/articleParagraphUtils";

export const IMAGE_SCOPES = {
  ARTICLES: "ARTICLES",
  NEWSLETTER: "NEWSLETTER",
};

const authHeaders = () => {
  const rawToken = sessionStorage.getItem("token");
  if (!rawToken) {
    throw new Error("Sessione scaduta. Effettua di nuovo l'accesso.");
  }
  return {
    headers: {
      Authorization: `Bearer ${JSON.parse(rawToken)}`,
    },
  };
};

const authMultipartHeaders = () => authHeaders();

const parseResponse = (parser, data, context) => {
  try {
    return parser(data);
  } catch (error) {
    throw new Error(formatSchemaParseError(error, context));
  }
};

const extractErrorMessage = (error, fallback) => {
  const backendError = error.response?.data;
  if (typeof backendError === "string") {
    return backendError;
  }
  if (backendError?.message) {
    return backendError.message;
  }
  if (backendError?.error) {
    return backendError.error;
  }
  return fallback;
};

const IMAGE_UPLOAD_ENDPOINTS = {
  ARTICLES: api.storageArticleImageUpload,
  NEWSLETTER: api.storageNewsletterImageUpload,
};

const IMAGE_LIST_ENDPOINTS = {
  ARTICLES: api.storageArticleImageList,
  NEWSLETTER: api.storageNewsletterImageList,
};

const resolveImageUploadEndpoint = (scope) => {
  const parsedScope = imageScopeSchema.parse(scope);
  const endpoint = IMAGE_UPLOAD_ENDPOINTS[parsedScope];
  if (!endpoint) {
    throw new Error(`Scope immagine non supportato: ${parsedScope}`);
  }
  return endpoint;
};

const resolveImageListEndpoint = (scope) => {
  const parsedScope = imageScopeSchema.parse(scope);
  const endpoint = IMAGE_LIST_ENDPOINTS[parsedScope];
  if (!endpoint) {
    throw new Error(`Scope immagine non supportato: ${parsedScope}`);
  }
  return endpoint;
};

const cleanStringArray = (values = []) =>
  values.map((value) => String(value || "").trim()).filter(Boolean);

export const stripArticleForSave = (article) => {
  const content = {
    version: 1,
    ...(article.content || {}),
    blocks: (article.content?.blocks || []).map((block) => {
      if (block.type === "image") {
        return {
          type: "image",
          objectKey: block.objectKey,
          publicUrl: block.publicUrl,
          alt: block.alt?.trim() || undefined,
        };
      }
      return sanitizeParagraphRunsForSave(block);
    }).filter((block) => block.type !== "paragraph" || block.runs.length > 0),
  };

  return articleSavePayloadSchema.parse({
    id: article.id || undefined,
    title: article.title?.trim() || "",
    subtitle: article.subtitle?.trim() || undefined,
    image: article.image?.trim() || "",
    content,
    category: article.category?.trim() || "",
    linkedArticles: cleanStringArray(article.linkedArticles).slice(0, 3),
    suggestedProducts: cleanStringArray(article.suggestedProducts),
    readingMinutes: Number(article.readingMinutes) || undefined,
    publishedAt: article.publishedAt || undefined,
    isVisible: Boolean(article.isVisible),
  });
};

export const listArticleCategories = async () => {
  try {
    const response = await axios.post(api.articleCategoriesList, {}, authHeaders());
    return parseResponse(parseArticleCategories, response.data, "recupero categorie magazine");
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel recupero delle categorie magazine"));
  }
};

export const saveArticleCategory = async (name) => {
  try {
    const response = await axios.post(api.articleCategoriesSave, { name: name.trim() }, authHeaders());
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel salvataggio della categoria"));
  }
};

export const deleteArticleCategory = async (name) => {
  try {
    await axios.post(api.articleCategoriesDelete, { name }, authHeaders());
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nell'eliminazione della categoria"));
  }
};

export const listArticles = async ({ category } = {}) => {
  try {
    const response = await axios.post(api.articlesList, { category: category || undefined }, authHeaders());
    return parseResponse(parseArticleSummaries, response.data, "recupero articoli");
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel recupero degli articoli"));
  }
};

export const getArticleById = async (id) => {
  try {
    const response = await axios.post(api.articlesById, { id }, authHeaders());
    return parseResponse(parseArticle, response.data, "caricamento articolo");
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel caricamento dell'articolo"));
  }
};

export const searchArticles = async ({ search, excludeId, limit = 20 }) => {
  try {
    const response = await axios.post(api.articlesSearch, { search, excludeId, limit }, authHeaders());
    return parseResponse(parseArticleSummaries, response.data, "ricerca articoli");
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nella ricerca degli articoli"));
  }
};

export const saveArticle = async (article) => {
  try {
    const response = await axios.post(api.articlesSave, stripArticleForSave(article), authHeaders());
    return parseResponse(parseArticle, response.data, "salvataggio articolo");
  } catch (error) {
    if (error instanceof Error && error.message && !error.response) {
      throw error;
    }
    throw new Error(extractErrorMessage(error, "Errore nel salvataggio dell'articolo"));
  }
};

export const deleteArticle = async (id) => {
  try {
    await axios.post(api.articlesDelete, { id }, authHeaders());
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nell'eliminazione dell'articolo"));
  }
};

export const toggleArticleVisibility = async (id, isVisible) => {
  const article = await getArticleById(id);
  return saveArticle({ ...article, isVisible });
};

export const uploadContentImage = async (file, scope) => {
  const formData = new FormData();
  formData.append("file", file);
  try {
    const response = await axios.post(resolveImageUploadEndpoint(scope), formData, authMultipartHeaders());
    return parseResponse(parseStorageUploadResult, response.data, "caricamento immagine");
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel caricamento dell'immagine"));
  }
};

export const listContentImages = async (scope) => {
  try {
    const response = await axios.post(resolveImageListEndpoint(scope), {}, authHeaders());
    return parseResponse(parseStorageObjectSummaries, response.data, "recupero immagini");
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel recupero delle immagini"));
  }
};

export const deleteContentImage = async ({ objectKey }) => {
  try {
    await axios.post(api.storageImageDelete, { objectKey }, authHeaders());
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nell'eliminazione dell'immagine"));
  }
};

export const listBanners = async () => {
  try {
    const response = await axios.post(api.bannersList, {}, authHeaders());
    return parseResponse(parseBanners, response.data, "recupero banner");
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel recupero dei banner"));
  }
};

export const uploadBanner = async (file, { posizione, deviceMode, isActive }) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("posizione", String(posizione));
  formData.append("deviceMode", deviceMode);
  formData.append("isActive", String(isActive));
  try {
    const response = await axios.post(api.bannersUpload, formData, authMultipartHeaders());
    return parseResponse(parseBanner, response.data, "upload banner");
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel caricamento del banner"));
  }
};

export const updateBanner = async (banner) => {
  const payload = bannerUpdatePayloadSchema.parse({
    objectKey: banner.objectKey,
    posizione: banner.posizione,
    deviceMode: banner.deviceMode,
    isActive: banner.isActive,
  });
  try {
    await axios.post(api.bannersUpdate, payload, authHeaders());
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel salvataggio del banner"));
  }
};

export const deleteBanner = async ({ objectKey }) => {
  try {
    await axios.post(api.bannersDelete, { objectKey }, authHeaders());
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nell'eliminazione del banner"));
  }
};
